import React, { useEffect } from "react";
import { PageLevel, useConsoleState } from "providers/ConsoleStateProvider";
import { useWindowSize } from "react-use";
import {
  ScnerioCardContainer,
  StyledLeftArea,
  StyledLi,
  StyledRightArea,
  StyledScenarioTextArea,
  StyledTitleArea,
  StyledUl,
  StyledScenarioPreview,
  StyledTextWithIconButton,
  StyledHeadline,
} from "./styled";
import { convertSentenceToSave, useScenario } from "./hook";
import { ScenarioPreview } from "pages/ScenarioManagement/ScenarioExpand";
import { useSenarioTemplateSet } from "pages/ScenarioManagement/ScenarioExpand/hook";
import { BackButton } from "components/parts/BackButton";
import { Typography } from "components/parts/Typography/Typography";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { usePageHeader } from "hooks/usePageHeader";
import { InterviewTypeIcon } from "components/parts/InterviewTypeIcon";
import { Button } from "components/parts/Button/Button";

type EscapeWordType = ":★:" | ":●:" | ":M:";

export const ScenarioManagement: React.FC = () => {
  const {
    selectedSubjectId,
    setScreenName,
    setPageLevel,
    selectedGroupId,
    selectedSubjectType,
  } = useConsoleState();
  const { pageHeader } = usePageHeader(selectedSubjectId);
  const { createScenario, reflesh, sentenceState, setSentenceState } =
    useScenario();
  const { fetchScenario } = useSenarioTemplateSet();
  const { width } = useWindowSize();

  const [titleAreaDOMRect, setTitleAreaDOMRect] = React.useState<DOMRect>();
  const titleAreaRef = React.useRef<HTMLDivElement>(null);
  const [cursorPosition, setCursorPosition] = React.useState(
    sentenceState.length
  );

  React.useEffect(() => {
    if (!titleAreaRef.current) return;
    const DOMRect = titleAreaRef.current.getBoundingClientRect();
    setTitleAreaDOMRect(DOMRect);
  }, [titleAreaRef, width]);

  const onClickHandoler = async () => {
    await createScenario(sentenceState, selectedSubjectId);
    await reflesh(selectedSubjectId);
    await fetchScenario();
  };

  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

  const updateCursorPosition = () => {
    if (textAreaRef.current) {
      setCursorPosition(textAreaRef.current.selectionStart);
    }
  };

  const insertToTextArea = (word: EscapeWordType) => {
    const newText =
      `${sentenceState.substring(0, cursorPosition)}` +
      word +
      `${sentenceState.substring(cursorPosition)}`;
    setSentenceState(newText);

    const setCursor = new Promise<void>((resolve) => {
      textAreaRef?.current?.focus();
      resolve();
    });

    setCursor.then(() => {
      textAreaRef?.current?.setSelectionRange(
        cursorPosition + word.length,
        cursorPosition + word.length
      );
      setCursorPosition(cursorPosition + word.length);
    });
  };

  const onChangeTextHandler: React.ComponentProps<"textarea">["onChange"] = (
    e
  ) => {
    setSentenceState(e.target.value);
    updateCursorPosition();
  };

  useEffect(() => {
    setScreenName("インタビューフロー");
    setPageLevel(PageLevel.fileAndScenarioAndMonitor);
    reflesh(selectedSubjectId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const arrangedSentenceState = () => {
    const previewSentence = convertSentenceToSave(
      sentenceState,
      selectedSubjectId,
      selectedGroupId
    );
    if (previewSentence.length !== 0) {
      const scenarioList = previewSentence?.filter(
        (scenarioData) => scenarioData.template_no === 0
      );

      const templateList = previewSentence?.filter(
        (scenarioData) => scenarioData.template_no !== 0
      );

      const arrangedScenario = scenarioList?.map((scenario) => {
        return {
          scenario: scenario,
          templates: templateList?.filter(
            (template) => template.scenario_no === scenario.scenario_no
          ),
        };
      });
      return arrangedScenario;
    } else {
      return [];
    }
  };

  const isSentenceCorrect = {
    startsWithoutStar: !sentenceState.split(/\r\n|\n/)[0].startsWith(":★:"),
    newLine: /^\n/.test(sentenceState),
  };

  return (
    <>
      <BackButton />
      <StyledHeadline>
        <InterviewTypeIcon interviewType={selectedSubjectType} size={24} />
        <Typography
          text={`${pageHeader.subjectName}のインタビューフロー`}
          size="xxlarge"
          weight={"bold"}
        />
      </StyledHeadline>
      <ScnerioCardContainer>
        <StyledLeftArea>
          <StyledTitleArea ref={titleAreaRef}>
            <Typography text="入力・編集" size="large" textAlign="center" />
            <StyledUl>
              <StyledLi>
                <Typography text="項目" weight="bold" size="small" />
                <Typography text="：行頭に" size="small" />
                <Button
                  text={"「:★:」を入力"}
                  variant="white"
                  onClick={() => insertToTextArea(":★:")}
                  size="medium"
                />
              </StyledLi>
              <StyledLi>
                <Typography text="テンプレート" weight="bold" size="small" />
                <Typography text="：行頭に" size="small" />
                <Button
                  text={"「:●:」を入力"}
                  variant="white"
                  onClick={() => insertToTextArea(":●:")}
                  size="medium"
                />
              </StyledLi>
              <StyledLi>
                <Typography text="改行" weight="bold" size="small" />
                <Typography text="：改行して入力" size="small" />
              </StyledLi>
              <StyledLi>
                <Typography text="モニター名" weight="bold" size="small" />
                <Typography text="：" size="small" />
                <Button
                  text={"「:M:」を入力"}
                  variant="white"
                  onClick={() => insertToTextArea(":M:")}
                  size="medium"
                />
              </StyledLi>
            </StyledUl>
          </StyledTitleArea>
          <StyledScenarioTextArea
            ref={textAreaRef}
            value={sentenceState}
            onChange={onChangeTextHandler}
            onSelect={updateCursorPosition}
          />
        </StyledLeftArea>

        <StyledRightArea>
          <StyledTitleArea minHeight={`${titleAreaDOMRect?.height}px`}>
            <Typography text="プレビュー" size="large" />
            <StyledTextWithIconButton
              onClick={onClickHandoler}
              text="保存"
              iconName={TextWithIconTypes.Edit}
              disabled={
                isSentenceCorrect.startsWithoutStar || isSentenceCorrect.newLine
              }
            />
          </StyledTitleArea>
          <StyledScenarioPreview>
            {isSentenceCorrect.newLine ? (
              <Typography
                text="一行目の先頭で改行することはできません。"
                size="small"
                color="errorText"
              />
            ) : isSentenceCorrect.startsWithoutStar ? (
              <Typography
                text="一行目の先頭は「:★:」を入力してください。"
                size="small"
                color="errorText"
              />
            ) : (
              <ScenarioPreview scenarioTemplateSet={arrangedSentenceState()} />
            )}
          </StyledScenarioPreview>
        </StyledRightArea>
      </ScnerioCardContainer>
    </>
  );
};
